import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { C, T } from './leadOverviewCardTokens';
import { OverviewCardTitleWithIcon, ClosedCard } from './overviewCardPrimitives';
import { abbreviateLongMonthNamesInString } from './overviewFormatUtils';
import {
  deriveLeadTemperatureUi,
  TemperatureIntentIcon,
  TemperatureSpectrumBar,
  temperatureIntentDisplayLabel,
  temperatureIntentHeroColor,
  temperatureIntentShortWord,
  isTemperatureNotSureIntent,
  resolveLeadTemperatureBarFillPct,
} from './leadTemperatureUtils';
import { overviewNaTypographySx } from './leadCoverageUtils';

const LEAD_TEMPERATURE_EMPTY_DESCRIPTION =
  'Hot, warm, or cold shows when call analysis or lead tags include it.';

const emptyStateDescriptionSx = {
  fontSize: '12px',
  fontWeight: 500,
  color: C.slate3,
  lineHeight: 1.55,
  maxWidth: 320,
  textAlign: 'center',
};

export function LeadTemperatureBadge({ leadTemperature, emptyTemperatureDescription = LEAD_TEMPERATURE_EMPTY_DESCRIPTION }) {
  const ui = deriveLeadTemperatureUi(leadTemperature);
  const showEmptyHelp = ui.kind !== 'intent' && emptyTemperatureDescription;
  return (
    <Box
      sx={{
        bgcolor: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: '14px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        p: '18px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <Box sx={{ mb: 1.25, flexShrink: 0 }}>
        <OverviewCardTitleWithIcon icon="🌡️" iconBg="#FFEDD5" title="Lead temperature" />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: showEmptyHelp ? 'column' : 'row',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          minHeight: showEmptyHelp ? 100 : 40,
          width: '100%',
        }}
      >
        {ui.kind !== 'intent' ? (
          <>
            <Typography sx={{ ...overviewNaTypographySx, fontSize: '22px' }}>N/A</Typography>
            {showEmptyHelp ? (
              <Typography sx={{ ...emptyStateDescriptionSx, px: 0.5 }}>{emptyTemperatureDescription}</Typography>
            ) : null}
          </>
        ) : (
          <>
            <TemperatureIntentIcon heroWord={ui.intentShort} rawIntent={ui.currentKey} sx={{ fontSize: 28 }} />
            <Chip
              label={ui.intentLabel || '—'}
              size="medium"
              sx={{
                height: 34,
                fontWeight: 800,
                fontSize: '0.85rem',
                borderRadius: '10px',
                ...(ui.notSureTemp
                  ? { bgcolor: C.bg2, color: C.slate3, border: `1px solid ${C.border}` }
                  : {
                      bgcolor: `${ui.intentColor}18`,
                      color: ui.intentColor,
                      border: `1px solid ${ui.intentColor}40`,
                    }),
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
}

/**
 * Closed overview: full spectrum + optional footer (aging / conversion / stats).
 */
export function LeadTemperatureClosedCard({
  leadTemperature,
  headerRight,
  closedCardSx,
  leadAging: la,
  statsData,
  conversionProb,
  hideFooter,
  isDeleted,
  emptyTemperatureDescription = LEAD_TEMPERATURE_EMPTY_DESCRIPTION,
}) {
  const ltNullClosed = leadTemperature == null;
  const ltClosed = leadTemperature || {};
  const jrClosed = Array.isArray(ltClosed.journey) ? ltClosed.journey : [];
  const curC = String(ltClosed.current_intent ?? '').trim();
  const lastJC = jrClosed.length ? String(jrClosed[jrClosed.length - 1]?.intent ?? '').trim() : '';
  const cKey = curC || lastJC;
  const hasIntentClosed = Boolean(cKey);
  const intentShortClosed = temperatureIntentShortWord(cKey);
  const intentLabelClosed = temperatureIntentDisplayLabel(cKey);
  const notSureClosed = isTemperatureNotSureIntent(cKey);
  const barFillClosed = resolveLeadTemperatureBarFillPct(ltClosed, intentShortClosed, cKey);
  const intentColorClosed = temperatureIntentHeroColor(intentShortClosed, cKey);

  return (
    <ClosedCard
      title="Lead temperature"
      iconEmoji="🌡️"
      iconBg="#FFEDD5"
      right={headerRight}
      headerIconSize={28}
      headerSx={{ mb: 1.25 }}
      sx={closedCardSx}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          textAlign: 'left',
          pt: 0,
          pb: 0.5,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            textAlign: 'left',
            width: '100%',
            gap: 1,
            mb: hideFooter ? 0 : 1.5,
            py: 0,
          }}
        >
          {ltNullClosed ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1, width: '100%' }}>
              <Typography sx={{ ...overviewNaTypographySx, textAlign: 'center' }}>N/A</Typography>
              {emptyTemperatureDescription ? (
                <Typography sx={{ ...emptyStateDescriptionSx, px: 0.5 }}>{emptyTemperatureDescription}</Typography>
              ) : null}
            </Box>
          ) : hasIntentClosed ? (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                <TemperatureIntentIcon heroWord={intentShortClosed} rawIntent={cKey} sx={{ fontSize: { xs: 32, sm: 36 } }} />
                <Typography
                  sx={{
                    color: intentColorClosed,
                    fontSize: { xs: '22px', sm: '26px' },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {intentLabelClosed}
                </Typography>
              </Box>
              {!notSureClosed ? (
                <>
                  <TemperatureSpectrumBar value={barFillClosed} thumbColor={intentColorClosed} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.25 }}>
                    <Typography sx={{ ...T.axisLabel, fontWeight: 600, color: '#2563EB' }}>Cold</Typography>
                    <Typography sx={{ ...T.axisLabel, fontWeight: 600, color: '#C2410C' }}>Hot</Typography>
                  </Box>
                </>
              ) : null}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1, width: '100%' }}>
              <Typography sx={{ ...overviewNaTypographySx, textAlign: 'center' }}>N/A</Typography>
              {emptyTemperatureDescription ? (
                <Typography sx={{ ...emptyStateDescriptionSx, px: 0.5 }}>{emptyTemperatureDescription}</Typography>
              ) : null}
            </Box>
          )}
        </Box>
        {!hideFooter ? (
          <>
            <Typography sx={{ ...T.bodyPrimary, mb: 1, fontWeight: 500 }}>
              {isDeleted && typeof la?.days_since_last_contact === 'number' ? (
                <>
                  {la.days_since_last_contact} days since last contact
                  {la?.aging_bucket ? ` · ${la.aging_bucket}` : ''}
                </>
              ) : (
                <>
                  {la?.aging_bucket ? `${la.aging_bucket} window` : '—'}
                  {la?.last_contacted_at ? ` · last contact ${la.last_contacted_at}` : ''}
                </>
              )}
            </Typography>
            {conversionProb?.recommended_action ? (
              <Typography sx={{ ...T.bodyPrimary, mb: 1, fontWeight: 500 }}>{conversionProb.recommended_action}</Typography>
            ) : null}
            <Box sx={{ display: 'grid', gap: '6px' }}>
              {(() => {
                const connectedCalls =
                  statsData?.connected_calls ?? statsData?.connected_call ?? statsData?.connected_pts ?? null;
                const connectedText =
                  typeof connectedCalls === 'number'
                    ? `${connectedCalls} connected calls`
                    : (statsData?.connected_pts ?? 0) > 0
                      ? 'connected calls present'
                      : 'calls logged';
                const notesCountRaw = statsData?.total_note ?? statsData?.total_notes ?? null;
                const notesNum =
                  notesCountRaw != null && notesCountRaw !== ''
                    ? Number(notesCountRaw)
                    : 0;
                const hasNotes = !Number.isNaN(notesNum) && notesNum > 0;
                const notesLine = hasNotes
                  ? `${notesNum} ${notesNum === 1 ? 'note' : 'notes'}`
                  : 'No notes added yet';
                return (
                  <>
                    <Typography sx={{ ...T.bodyPrimary, fontWeight: 500 }}>Recent interaction · {connectedText}</Typography>
                    <Typography sx={{ ...T.bodyPrimary, fontWeight: 500 }}>
                      · {notesLine}
                    </Typography>
                  </>
                );
              })()}
            </Box>
            {isDeleted && la?.last_contacted_at ? (
              <Typography sx={{ ...T.bodyPrimary, fontWeight: 500, color: C.slate3, mt: 0.75 }}>
                No recent outreach · last contact {abbreviateLongMonthNamesInString(la.last_contacted_at)}
              </Typography>
            ) : null}
          </>
        ) : null}
      </Box>
    </ClosedCard>
  );
}

/**
 * @param {'badge' | 'closed'} variant
 */
export default function LeadTemperature({ variant = 'badge', emptyTemperatureDescription, ...rest }) {
  if (variant === 'closed') {
    return (
      <LeadTemperatureClosedCard
        emptyTemperatureDescription={emptyTemperatureDescription}
        {...rest}
      />
    );
  }
  return (
    <LeadTemperatureBadge
      leadTemperature={rest.leadTemperature}
      emptyTemperatureDescription={emptyTemperatureDescription}
    />
  );
}
